import { delete as trash } from '@ibinder/common-icons';
import {
  createRecurrenceValue,
  Flexbox,
  ThemeButton
} from '@ibinder/ui-common-core';
import { ThemeSideSheet } from '@ibinder/ui-common-dialogs';
import { buttonVariants } from '@ibinder/ui-common-styles';
import { isEqual } from 'lodash';
import { validate as uuidValidate } from 'uuid';

import { useMutation, useQuery } from '@apollo/client';
import {
  iBinderHeaderEnum,
  Region,
  ResultColorOptions,
  ResultTypeEnum
} from '@ibinder/ui-common-models';

import { useIBTranslation } from '@ibinder/ui-common-hooks';
import React, { useEffect, useState } from 'react';
import {
  GET_ALL_GLOBAL_INSPECTION_TEMPLATES,
  GET_GLOBAL_INSPECTION_CATEGORIES
} from '../../../api/queries/queries_local';
import { getRecurrenceUnitOptions } from '../inspectionTypeTransformations';
import {
  ColorOptions,
  InspectionTemplate,
  InspectionTemplateFormRenderingStructure,
  issueFormSections,
  noTemplate
} from './InspectionTemplateFormRenderingStructure.data';
import { InspectionLabels } from './translationHelpers';

import { KeyValue } from '@ibinder/common-frontend';
import { FormRenderer } from '@ibinder/ui-common-forms';
import styled from 'styled-components';
import {
  CREATE_GLOBAL_INSPECTION_TYPE,
  CREATE_INSPECTION_CATEGORY,
  DUPLICATE_INSPECTION_TYPE,
  EDIT_GLOBAL_INSPECTION_TYPE
} from '../../../api/mutations/inspectionTemplate.query';
import { FORM_MODES } from '../InspectionTypeTemplates';

export enum ModeType {
  View = 'view',
  Edit = 'edit',
  Create = 'create'
}

const DuplicateThemeButton = styled(ThemeButton)`
  margin-right: 1rem;
`;

interface Props {
  onClose?: () => void;
  labels?: InspectionLabels;
  setMode?: (mode: FORM_MODES) => void;
  mode?: FORM_MODES;
  isCompliant?: boolean;
  inspectionType?: any;
  market?: string;
  marketList?: any[];
  contractorList?: any[];
  workflowTemplateList?: any[];
  usePrefixIdForIssues?: boolean;
}

const INITIAL_RECURRENCE_VALUE = 'P1M';
const INITIAL_RECURRENCE_WINDOW_VALUE = 'P1W';

const interpretationDefault = {
  aboveValueColor: ResultColorOptions.find(
    (item) => item.Value === ColorOptions.Green
  )?.Key,
  belowValueColor: ResultColorOptions.find(
    (item) => item.Value === ColorOptions.Red
  )?.Key,
  thresholdValue: ''
};

const CreateInspectionTemplateSideSheet = ({
  onClose,
  labels,
  market = null,
  mode = FORM_MODES.CREATE,
  setMode,
  isCompliant = false,
  inspectionType = null,
  marketList = [],
  contractorList = [],
  workflowTemplateList = [],
  usePrefixIdForIssues = false
}: Props) => {
  const marketInfo = [
    ...(marketList
      ?.map((item) => ({
        Value: item.name,
        Key: item.country.countryCode
      }))
      ?.sort((a, b) => a.Value.localeCompare(b.Value)) || [])
  ];
  const { t } = useIBTranslation();
  const userId = localStorage.getItem('userId');
  const editMode = mode === FORM_MODES.EDIT;
  const duplicateMode = mode === FORM_MODES.DUPLICATE;
  const useExistingMode = editMode || duplicateMode;
  const [formForceUpdate, setFormForceUpdate] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null);
  const [newAdditionalDocuments, setNewAdditionalDocuments] = useState([]);
  const [newLeniencyDocuments, setNewLeniencyDocuments] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState<KeyValue[]>([]);
  const [recurrenceOptions, setRecurrenceOptions] = useState(
    editMode ? inspectionType.showRecurrenceOptions : true
  );
  const [reminderOptions, setReminderOptions] = useState(
    editMode ? inspectionType.showReminderOptions : true
  );

  const [selectedMarket, setSelectedMarket] = useState(market);
  const [inspectionInterval, setInspectionInterval] = useState(
    useExistingMode
      ? `P${inspectionType.inspectionInterval?.interval}${inspectionType.inspectionInterval?.unit}`
      : INITIAL_RECURRENCE_VALUE
  );

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const defaultChoices = [
    {
      name: labels.approved,
      color: ResultColorOptions.find(
        (item) => item.Value === ColorOptions.Green
      )?.Key
    },
    {
      name: labels.approvedWithRemarks,
      color: ResultColorOptions.find(
        (item) => item.Value === ColorOptions.Yellow
      )?.Key
    },
    {
      name: labels.notApproved,
      color: ResultColorOptions.find((item) => item.Value === ColorOptions.Red)
        ?.Key
    }
  ];

  const emptyInspectionTemplate: InspectionTemplate = {
    id: '',
    name: null,
    description: null,
    inspectionCategory: '',
    disabled: false,
    appCompatible: false,
    propertyStructureCompatible: false,
    showRecurrenceOptions: false,
    showReminderOptions: false,
    market: '',
    setDefaultContractor: false,
    defaultContractor: null,
    remindBeforeDueDate: null,
    supervisorRole: {},
    leniencyDocuments: [],
    additionalDocuments: [],
    appType: [],
    inspectionPlanResultSettings: {
      resultType: ResultTypeEnum.MultipleChoice,
      resultChoices: defaultChoices
    },
    inspectionOccasionResultSettings: {
      resultType: ResultTypeEnum.MultipleChoice,
      resultChoices: defaultChoices,
      unitType: {
        metric: '',
        interpretation: interpretationDefault
      }
    },

    inspectionInterval: {
      type: '',
      interval: '1',
      unit: 'M'
    },
    contractor: {
      type: 'noDefault',
      selectedContractor: ''
    },
    workflowTemplateId: '',
    uniqueNumberCountOnOccasionLevel: false,
  };

  function getRegionHeaders() {
    if (isCompliant)
      return {
        [iBinderHeaderEnum.TENANTID]: '', // Needs to be empty string to override automatic set tenantId
        [iBinderHeaderEnum.REGION]: Region.Eu
      };
    return {
      [iBinderHeaderEnum.REGION]: Region.International
    };
  }

  const selectedInspectionTemplate = () => {
    if (inspectionType) {
      const workflowTemplateId = inspectionType.workflowTemplateId;

      const currentInspectionType: InspectionTemplate = {
        id: inspectionType.id,
        name: !duplicateMode ? inspectionType.name : '',
        description: !duplicateMode ? inspectionType.description : '',
        inspectionCategory: inspectionType.inspectionCategory.id,
        disabled: inspectionType.disabled,
        appCompatible: inspectionType.appCompatible,
        propertyStructureCompatible: inspectionType.propertyStructureCompatible,
        showRecurrenceOptions: inspectionType.showRecurrenceOptions,
        showReminderOptions: inspectionType.showReminderOptions,
        market: inspectionType.market,
        remindBeforeDueDate: inspectionType.remindBeforeDueDate,
        supervisorRole: inspectionType.supervisorRole,
        leniencyDocuments: inspectionType.leniencyDocuments,
        additionalDocuments: inspectionType.additionalDocuments,
        setDefaultContractor: inspectionType.setDefaultContractor,
        defaultContractor: inspectionType.defaultContractor,
        appType: [
          ...(inspectionType.propertyStructureCompatible ? ['property'] : []),
          ...(inspectionType.appCompatible ? ['app'] : [])
        ],
        inspectionPlanResultSettings: {
          resultType:
            inspectionType?.inspectionPlanResultSettings?.resultType ??
            ResultTypeEnum.NoResult,
          resultChoices: !inspectionType?.inspectionPlanResultSettings
            ? defaultChoices
            : inspectionType?.inspectionPlanResultSettings?.resultChoices ?? []
        },
        inspectionOccasionResultSettings: {
          ...inspectionType.inspectionOccasionResultSettings,
          resultType:
            inspectionType.inspectionOccasionResultSettings?.resultType ??
            ResultTypeEnum.MultipleChoice,
          resultChoices: !inspectionType?.inspectionOccasionResultSettings
            ? defaultChoices
            : inspectionType.inspectionOccasionResultSettings?.resultChoices ??
              [],
          unitType: {
            ...inspectionType.inspectionOccasionResultSettings?.unitType,
            interpretation:
              inspectionType.inspectionOccasionResultSettings?.unitType
                .interpretation ?? interpretationDefault
          }
        },

        inspectionInterval: inspectionType.inspectionInterval
          ? {
              type: 'suggestedInterval',
              interval: inspectionType.inspectionInterval
                .replace('P', '')
                .slice(0, -1),

              unit: inspectionType.inspectionInterval.charAt(
                inspectionType.inspectionInterval.length - 1
              )
            }
          : {
              type: 'noRecurrentInterval',
              interval: '',
              unit: ''
            },
        contractor: {
          type: inspectionType.setDefaultContractor
            ? 'setDefault'
            : 'noDefault',
          selectedContractor: inspectionType.defaultContractor
        },
        workflowTemplateId: workflowTemplateId || noTemplate,
        uniqueNumberCountOnOccasionLevel: inspectionType.uniqueNumberCountOnOccasionLevel,
      };

      return currentInspectionType;
    } else {
      emptyInspectionTemplate;
    }
  };

  const [inspectionTemplate, setInspectionTemplate] =
    useState<InspectionTemplate>(
      selectedInspectionTemplate() ?? emptyInspectionTemplate
    );

  const inspectionCategories = useQuery(GET_GLOBAL_INSPECTION_CATEGORIES, {
    variables: {
      market: selectedMarket
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !selectedMarket,
    context: {
      headers: getRegionHeaders()
    },
    onCompleted: (data) => {
      setCategoryOptions(
        data?.inspectionCategories
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item) => ({
            Value: item.name,
            Key: item.id
          })) ?? []
      );
    }
  });

  const recurrenceUnitOptions = getRecurrenceUnitOptions(t);

  /* Mutation */
  const [createInspectionType] = useMutation(CREATE_GLOBAL_INSPECTION_TYPE, {
    onCompleted: onClose
  });
  const [editInspectionType] = useMutation(EDIT_GLOBAL_INSPECTION_TYPE, {
    onCompleted: onClose
  });
  const [duplicateInspectionType] = useMutation(DUPLICATE_INSPECTION_TYPE, {
    onCompleted: onClose
  });

  const selectedCategory = categoryOptions?.find(
    (item) => item.Key === inspectionTemplate.inspectionCategory
  );

  const selectedInspectionTypeId = useExistingMode && inspectionType.id;
  const uploadFiles = async (docs) => {
    if (docs || docs.length > 0) {
      const promises = docs.map(async (doc) => {
        const data = await putUploadS3Blob(doc);
        return { name: doc?.file?.name || doc.name, id: data.uploadId };
      });
      return await Promise.all(promises);
    } else {
      return [];
    }
  };
  const putUploadS3Blob = async (file) => {
    const uploadRespons = await fetch(
      '/api/documents/uploads/personalstorage/presignedupload',
      {
        mode: 'cors',
        method: 'GET',
        headers: {
          ...getRegionHeaders(),
          authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      }
    );
    const uploadResponsJson = await uploadRespons.json();

    const getUploadHeaders = () => {
      if (isCompliant)
        return {
          'Content-Type': '',
          'Content-Length': file?.size
        };
      return {
        'Content-Type': file?.type,
        'Content-Length': file?.size,
        'x-ms-blob-type': 'BlockBlob'
      };
    };

    const fileUploaded = await fetch(uploadResponsJson.presignedUrl, {
      mode: 'cors',
      method: 'PUT',
      headers: getUploadHeaders(),
      body: file.file
    });

    if (!fileUploaded.ok) {
      console.error('File upload failed', fileUploaded);
      return null;
    }
    return uploadResponsJson;
  };

  const [createInspectionCategory] = useMutation(CREATE_INSPECTION_CATEGORY, {
    context: {
      headers: getRegionHeaders()
    }
  });

  useEffect(() => {
    if (selectedCategoryKey) {
      if (
        categoryOptions.find(
          (item) => item.Key === item.Value && item.Key === selectedCategoryKey
        )
      ) {
        onCreateNewCategory(selectedCategoryKey);
      }
    }
  }, [selectedCategoryKey]);

  const onCreateNewCategory = (newCategory) => {
    createInspectionCategory({
      variables: {
        input: {
          name: newCategory,
          market: inspectionTemplate.market
        }
      },
      refetchQueries: [GET_GLOBAL_INSPECTION_CATEGORIES],
      onCompleted: ({ InspectionCategory }) => {
        setSelectedCategoryKey(null);

        setInspectionTemplate({
          ...inspectionTemplate,
          inspectionCategory: InspectionCategory.id
        });
        setFormForceUpdate(true);
      }
    });
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    let inspectionOccasionResultSettings =
      inspectionTemplate.inspectionOccasionResultSettings;
    if (
      inspectionOccasionResultSettings.resultType ===
      ResultTypeEnum.TextWithUnit
    ) {
      if (
        inspectionOccasionResultSettings.unitType.interpretation
          .thresholdValue === ''
      ) {
        inspectionOccasionResultSettings.unitType.interpretation = null;
      }
    } else {
      inspectionOccasionResultSettings.unitType.interpretation = null;
    }

    const leniencyDocs = inspectionType?.leniencyDocuments ?? [];
    const additionalDocs = inspectionType?.additionalDocuments ?? [];

    const workflowTemplateId = inspectionTemplate.workflowTemplateId;
    const postBody: any = {
      name: inspectionTemplate.name,
      description: inspectionTemplate.description,
      market: selectedMarket,
      inspectionCategory: {
        Id: selectedCategory.Key,
        Name: selectedCategory.Value
      },
      appCompatible: inspectionTemplate.appType.includes('app'),
      propertyStructureCompatible:
        inspectionTemplate.appType.includes('property'),
      disabled: false,

      showRecurrenceOptions: recurrenceOptions,
      showReminderOptions: reminderOptions,
      inspectionInterval:
        recurrenceOptions && inspectionInterval
          ? inspectionTemplate.inspectionInterval.type !== 'noRecurrentInterval'
            ? createRecurrenceValue(
                `P${inspectionTemplate.inspectionInterval.interval}${inspectionTemplate.inspectionInterval.unit}`
              )
            : null
          : null,
      remindBeforeDueDate: null,
      supervisorRole: null,
      leniencyDocuments: [
        ...inspectionTemplate.leniencyDocuments.filter(
          (doc) => !newLeniencyDocuments.some((newDoc) => newDoc.id === doc.id)
        ),
        ...(await uploadFiles(newLeniencyDocuments))
      ],
      additionalDocuments: [
        ...inspectionTemplate.additionalDocuments.filter(
          (doc) =>
            !newAdditionalDocuments.some((newDoc) => newDoc.id === doc.id)
        ),
        ...(await uploadFiles(newAdditionalDocuments))
      ],
      inspectionPlanResultSettings:
        inspectionTemplate.inspectionPlanResultSettings,
      inspectionOccasionResultSettings:
        inspectionTemplate.inspectionOccasionResultSettings,
      contractor: inspectionTemplate.contractor,
      defaultContractor: inspectionTemplate.contractor.selectedContractor,
      setDefaultContractor: inspectionTemplate.contractor.type === 'setDefault',
      workflowTemplateId:
        workflowTemplateId && workflowTemplateId !== noTemplate
          ? workflowTemplateId
          : null,
      uniqueNumberCountOnOccasionLevel: inspectionTemplate.uniqueNumberCountOnOccasionLevel,
    };
    if (mode === FORM_MODES.CREATE) {
      createInspectionType({
        variables: {
          input: postBody
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    } else if (editMode) {
      editInspectionType({
        variables: {
          input: postBody,
          inspectionTypeId: selectedInspectionTypeId
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    } else if (duplicateMode) {
      duplicateInspectionType({
        variables: {
          input: postBody,
          inspectionTypeId: selectedInspectionTypeId
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    }
  };
  const transformedArray = inspectionTemplate?.additionalDocuments?.reduce(
    (acc, item) => {
      if (!item.metadata) {
        item.metadata = []; // Add metadata field if not present
        item.files = item?.files ?? {};
      }
      acc[item?.id] = item;
      return acc;
    },
    {}
  );

  const leniencyDocumentsTransformedArray =
    inspectionTemplate?.leniencyDocuments?.reduce((acc, item) => {
      if (!item.metadata) {
        item.metadata = []; // Add metadata field if not present
        item.files = item?.files ?? {};
      }
      acc[item?.id] = item;
      return acc;
    }, {});
  const lookupDocData = {
    ...transformedArray,
    ...leniencyDocumentsTransformedArray
  };
  /**
   * This function is used to handle new documents with existing documents
   * to add metadata and files fields if not present
   * and required for the form to show the documents before
   * @param newDocs
   * @param existingDocs
   * @param setNewDocs
   * @returns
   */
  const handleNewDocuments = (newDocs, existingDocs, setNewDocs) => {
    if (!isEqual(newDocs, existingDocs)) {
      const addedDocuments = newDocs.filter(
        (doc) => !existingDocs.some((existingDoc) => existingDoc.id === doc.id)
      );

      setNewDocs(addedDocuments);
    }
  };
  const workflowTemplateData = {
    workflowTemplateList
  };

  const hasWorkflowTemplateSelected =
    inspectionTemplate?.workflowTemplateId &&
    inspectionTemplate?.workflowTemplateId !== noTemplate;

  const shouldShowIssueNumbering = usePrefixIdForIssues && hasWorkflowTemplateSelected;

  return (
    <ThemeSideSheet
      headerText={
        mode === FORM_MODES.CREATE || mode === FORM_MODES.DUPLICATE
          ? labels.createInspectionTypeHeader
          : labels.editInspectionTypeHeader
      }
      open={true}
      onClose={onClose}
      marginTop="4.5rem"
      footer={
        <Flexbox justifyContent={'space-between'}>
          {
            <>
              <ThemeButton
                buttonType={buttonVariants.Secondary}
                onClick={onClose}
              >
                {labels.cancel}
              </ThemeButton>
              <Flexbox>
                {mode === FORM_MODES.EDIT && (
                  <DuplicateThemeButton
                    buttonType={buttonVariants.Primary}
                    disabled={hasSubmitted}
                    onClick={() => {
                      setMode(FORM_MODES.DUPLICATE);
                    }}
                  >
                    {labels.duplicate}
                  </DuplicateThemeButton>
                )}
                <ThemeButton
                  buttonType={buttonVariants.Primary}
                  onClick={() => {
                    setHasSubmitted(true);
                    handleSubmit();
                  }}
                  disabled={
                    hasSubmitted ||
                    !inspectionTemplate.name ||
                    !inspectionTemplate.description ||
                    inspectionTemplate.market === '' ||
                    (inspectionTemplate.market !== null &&
                      inspectionTemplate.inspectionCategory === '')
                  }
                >
                  {mode === FORM_MODES.CREATE || mode === FORM_MODES.DUPLICATE
                    ? labels.create
                    : labels.save}
                </ThemeButton>
              </Flexbox>
            </>
          }
        </Flexbox>
      }
    >
      <>
        <FormRenderer<InspectionTemplate>
          key={`${inspectionTemplate.id}-${inspectionTemplate.workflowTemplateId}`}
          language={undefined}
          issueFormSections={issueFormSections({
            editMode,
            inspectionTemplate,
            labels,
            marketOptions: marketInfo,
            categoryOptions,
            recurrenceUnitOptions,
            workflowTemplateData,
            shouldShowIssueNumbering,
            contractorsOptions: contractorList
              .map((item) => ({
                Value: t(item.name),
                Key: item.name
              }))
              .sort((a, b) => a.Value.localeCompare(b.Value))
          })}
          setForceUpdate={setFormForceUpdate}
          issueTemplate={InspectionTemplateFormRenderingStructure}
          sectionHeader={labels.formHeading}
          showDefaultIcon={false}
          canLinkFromProject={false}
          issue={inspectionTemplate}
          autoSave={false}
          forceUpdate={formForceUpdate}
          save={async (inspectionTemplateFormData, fieldsChanged) => {
            if (fieldsChanged === 'metadata') {
              return;
            }
            if (
              inspectionTemplateFormData.inspectionCategory &&
              !uuidValidate(inspectionTemplateFormData.inspectionCategory)
            ) {
              if (
                inspectionTemplateFormData.inspectionCategory !==
                inspectionTemplate.inspectionCategory
              ) {
                setSelectedCategoryKey(
                  inspectionTemplateFormData.inspectionCategory
                );
              }
            }

            handleNewDocuments(
              inspectionTemplateFormData.leniencyDocuments,
              inspectionTemplate.leniencyDocuments,
              setNewLeniencyDocuments
            );

            handleNewDocuments(
              inspectionTemplateFormData.additionalDocuments,
              inspectionTemplate.additionalDocuments,
              setNewAdditionalDocuments
            );
            setSelectedMarket(inspectionTemplateFormData.market);

            if (!inspectionTemplateFormData.market) {
              inspectionTemplateFormData.inspectionCategory = '';
            }

            setInspectionTemplate({
              ...inspectionTemplateFormData,
              setDefaultContractor:
                inspectionTemplateFormData.contractor.type === 'setDefault',
              defaultContractor:
                inspectionTemplateFormData.contractor.selectedContractor,
              inspectionInterval: {
                ...inspectionTemplateFormData.inspectionInterval,
                interval:
                  inspectionTemplateFormData.inspectionInterval.type ===
                  'noRecurrentInterval'
                    ? ''
                    : inspectionTemplateFormData.inspectionInterval.interval,
                unit:
                  inspectionTemplateFormData.inspectionInterval.type ===
                  'noRecurrentInterval'
                    ? ''
                    : inspectionTemplateFormData.inspectionInterval.unit
              }
            });
          }}
          ariaLabels={{
            saveButton: 'Save Inspection Template',
            cancelButton: 'Cancel'
          }}
          labels={{
            genTabTypes: function (a: string): string {
              throw new Error('Function not implemented.');
            },
            smallBinderSearchLabel: ''
          }}
          documentsLookup={lookupDocData}
          nodeData={undefined}
          binderInformation={undefined}
          customFileUpload={{
            enabled: true,
            uploadOptions: ['id', 'size', 'type', 'name']
          }}
          documentsLoading={false}
          documentActionMenu={[
            {
              action: 'delete',
              icon: trash
            }
          ]}
          tenantId={''}
        />
      </>
    </ThemeSideSheet>
  );
};

export default CreateInspectionTemplateSideSheet;
